// SearchInner — useSearchParams kullanan gerçek Search içeriği.
// Search.tsx tarafından Suspense boundary içinde render edilir.
"use client";
import { useCallback, useEffect } from "react";
import useSearch from "@/app/lib/features/search/search.hook";
import useSearchState from "@/app/hooks/useSearchState";
import useSpeechInput from "@/app/hooks/useSpeechInput";
import useTypewriterPlaceholder from "@/app/hooks/useTypewriterPlaceholder";
import SearchInput from "./SearchInput";
import SearchDropdown from "./SearchDropdown";
import SearchMobileSheet from "./SearchMobileSheet";

interface SearchInnerProps {
  onPhotoSearchOpen?: () => void;
}

const SearchInner = ({ onPhotoSearchOpen }: SearchInnerProps) => {
  const { value, setValue, isOpen, open, close, submit, containerRef } =
    useSearchState();

  const { placeholders, fetchHomeSearchList } = useSearch();

  useEffect(() => {
    fetchHomeSearchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placeholder = useTypewriterPlaceholder(placeholders, !!value);

  const {
    isListening,
    isSupported,
    micPermission,
    status,
    interimTranscript,
    toggle,
    stop,
  } = useSpeechInput({ onTranscript: setValue });

  const handleClose = useCallback(() => {
    stop();
    close();
  }, [stop, close]);

  const handleSelect = useCallback(
    (term: string) => {
      setValue(term);
    },
    [setValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && value.trim()) {
        e.preventDefault();
        stop();
        submit(value);
      }
      if (e.key === "Escape") handleClose();
    },
    [value, submit, stop, handleClose],
  );

  const inputProps = {
    value,
    onChange: setValue,
    onKeyDown: handleKeyDown,
    placeholder,
    isListening,
    isSupported,
    interimTranscript,
    micPermission,
    status,
    onMicToggle: toggle,
    onClear: () => {
      setValue("");
      stop();
    },
    onCameraClick: onPhotoSearchOpen,
  };

  return (
    <>
      <div className="relative" ref={containerRef}>
        <SearchInput {...inputProps} onFocus={open} />
        {/* Desktop dropdown — CSS transition, useMountTransition içinde */}
        <div className="relative hidden md:block overflow-visible">
          <SearchDropdown
            isOpen={isOpen}
            onClose={handleClose}
            query={value}
            onSelect={handleSelect}
          />
        </div>
      </div>

      {/* Mobile sheet — CSS transition, useMountTransition içinde */}
      <SearchMobileSheet
        isOpen={isOpen}
        onClose={handleClose}
        inputProps={inputProps}
        query={value}
        onSelect={handleSelect}
      />
    </>
  );
};

export default SearchInner;